import { useState, useEffect, useCallback } from 'react';
import { MediaMagicProvider, MagicImage } from '@media-magic/sdk';
import { ImageGrid } from '../components/gallery/ImageGrid';
import { ImageUpload } from '../components/upload/ImageUpload';
import { listImages, deleteImage } from '../services/galleryApi';
import { getImageAnalytics, type ImageAnalyticsData } from '../services/dashboardApi';
import type { ImageMetadata, ListImagesResponse } from '../types/image';

interface GalleryProps {
  userId: string;
}

const CDN_URL = import.meta.env.VITE_MM_CDN_URL || '';

/**
 * Gallery page component for displaying and managing user images
 */
export function Gallery({ userId }: GalleryProps): JSX.Element {
  const [images, setImages] = useState<ImageMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [selectedImage, setSelectedImage] = useState<ImageMetadata | null>(null);
  const [selectedImageAnalytics, setSelectedImageAnalytics] = useState<ImageAnalyticsData | null>(null);
  const [selectedImageAnalyticsLoading, setSelectedImageAnalyticsLoading] = useState(false);
  const [selectedImageAnalyticsError, setSelectedImageAnalyticsError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [fullImageLoaded, setFullImageLoaded] = useState(false);
  const isCompactModal = typeof window !== 'undefined' && window.matchMedia('(max-width: 900px)').matches;
  const selectedAspectRatio = selectedImage?.width && selectedImage?.height
    ? `${selectedImage.width} / ${selectedImage.height}`
    : undefined;

  // Fetch images on mount
  const fetchImages = useCallback(async (cursor?: string) => {
    try {
      setLoading(true);
      setError(null);

      const response: ListImagesResponse = await listImages(userId, 50, cursor);

      if (cursor) {
        // Append to existing images
        setImages((prev) => [...prev, ...response.images]);
      } else {
        // Replace images
        setImages(response.images);
      }

      setNextCursor(response.nextCursor);
      setTotalCount(response.totalCount ?? 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load images');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  // Handle delete
  const handleDelete = async (mediaId: string) => {
    try {
      await deleteImage(userId, mediaId);
      setImages((prev) => prev.filter((img) => img.mediaId !== mediaId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete image');
    }
  };

  // Handle view (open modal)
  const handleView = (image: ImageMetadata) => {
    setFullImageLoaded(false);
    setSelectedImage(image);
  };

  // Handle load more
  const handleLoadMore = async () => {
    if (nextCursor && !loadingMore) {
      setLoadingMore(true);
      try {
        const response: ListImagesResponse = await listImages(userId, 50, nextCursor);
        setImages((prev) => [...prev, ...response.images]);
        setNextCursor(response.nextCursor);
        setTotalCount(response.totalCount ?? 0);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load more images');
      } finally {
        setLoadingMore(false);
      }
    }
  };

  // Close modal
  const closeModal = () => {
    setSelectedImage(null);
    setSelectedImageAnalytics(null);
    setSelectedImageAnalyticsError(null);
    setSelectedImageAnalyticsLoading(false);
  };

  // Dismiss modal on Escape key
  useEffect(() => {
    if (!selectedImage) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [selectedImage]);

  useEffect(() => {
    let cancelled = false;

    const fetchImageAnalytics = async () => {
      if (!selectedImage) {
        return;
      }
      setSelectedImageAnalyticsLoading(true);
      setSelectedImageAnalyticsError(null);
      try {
        const data = await getImageAnalytics(userId, selectedImage.mediaId);
        if (!cancelled) {
          setSelectedImageAnalytics(data);
        }
      } catch (err) {
        if (!cancelled) {
          setSelectedImageAnalyticsError(err instanceof Error ? err.message : 'Failed to load image analytics');
        }
      } finally {
        if (!cancelled) {
          setSelectedImageAnalyticsLoading(false);
        }
      }
    };

    fetchImageAnalytics();
    return () => {
      cancelled = true;
    };
  }, [selectedImage, userId]);

  // Handle upload complete - poll for new image
  const handleUploadComplete = async () => {
    setProcessing(true);
    // Store the first image's mediaId to detect when a new image appears
    const previousFirstMediaId = images.length > 0 ? images[0].mediaId : null;

    // Poll every 2 seconds for up to 30 seconds
    let attempts = 0;
    const maxAttempts = 15;

    const poll = async () => {
      attempts++;
      try {
        const response: ListImagesResponse = await listImages(userId, 50);
        // Check if a new image appeared at the beginning (most recent)
        const newFirstMediaId = response.images.length > 0 ? response.images[0].mediaId : null;
        if (newFirstMediaId && newFirstMediaId !== previousFirstMediaId) {
          // New image found
          setImages(response.images);
          setNextCursor(response.nextCursor);
          setProcessing(false);
          return;
        }
      } catch {
        // Ignore errors during polling
      }

      if (attempts < maxAttempts) {
        setTimeout(poll, 2000);
      } else {
        // Timeout - refresh anyway
        setProcessing(false);
        fetchImages();
      }
    };

    // Start polling after initial delay
    setTimeout(poll, 2000);
  };

  return (
    <MediaMagicProvider
      config={{
        clientId: userId,
        cdnUrl: CDN_URL,
        assets: ['assets', 'test-assets'],
      }}
    >
      <div className="portal-surface" style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px',
          marginBottom: '24px',
        }}
      >
        <div>
          <h1 style={{ margin: 0 }}>Visual Library</h1>
          <p style={{ margin: '6px 0 0', color: 'var(--ink-600)', fontSize: '14px' }}>
            Review your assets and how they perform in real delivery.
          </p>
        </div>
        <div style={{ color: 'var(--ink-600)', display: 'flex', alignItems: 'center', gap: '12px' }}>
          {processing && (
            <span style={{ color: 'var(--primary-600)', fontSize: '14px' }}>
              Finalizing upload...
            </span>
          )}
          <span>
            {totalCount > 0
              ? `${images.length} of ${totalCount} image${totalCount !== 1 ? 's' : ''}`
              : `${images.length} image${images.length !== 1 ? 's' : ''}`
            }
          </span>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div
          style={{
            padding: '12px 16px',
            backgroundColor: 'var(--danger-100)',
            color: 'var(--danger-700)',
            borderRadius: '4px',
            marginBottom: '16px',
          }}
        >
          {error}
          <button
            onClick={() => fetchImages()}
            style={{
              marginLeft: '16px',
              padding: '4px 12px',
              backgroundColor: 'var(--danger-700)',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Image grid */}
      <ImageGrid
        images={images}
        loading={loading}
        loadingMore={loadingMore}
        totalCount={totalCount}
        onDelete={handleDelete}
        onView={handleView}
        onLoadMore={handleLoadMore}
        hasMore={nextCursor !== null}
        uploadCard={<ImageUpload userId={userId} onUploadComplete={handleUploadComplete} />}
      />

      {/* Image detail modal */}
      {selectedImage && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={closeModal}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              width: isCompactModal ? '94vw' : 'min(1120px, 92vw)',
              height: isCompactModal ? '88vh' : 'min(820px, 88vh)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div
              style={{
                padding: '16px',
                borderBottom: '1px solid var(--line-soft)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <h2 style={{ margin: 0, fontSize: '18px' }}>
                {selectedImage.originalFilename || selectedImage.originalKey?.split('/').pop() || selectedImage.mediaId}
              </h2>
              <button
                onClick={closeModal}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: 'var(--ink-600)',
                }}
              >
                &times;
              </button>
            </div>

            {/* Modal content */}
            <div
              style={{
                display: 'flex',
                flexDirection: isCompactModal ? 'column' : 'row',
                overflow: 'hidden',
                flex: 1,
                minHeight: 0,
              }}
            >
              {/* Image preview */}
              <div
                style={{
                  flex: isCompactModal ? '0 0 56%' : '0 0 68%',
                  width: isCompactModal ? '100%' : '68%',
                  padding: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'var(--surface-night-950)',
                  overflow: 'hidden',
                  minWidth: 0,
                  minHeight: isCompactModal ? 0 : '320px',
                  position: 'relative',
                }}
              >
                <div
                  style={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    minHeight: 0,
                  }}
                >
                  <MagicImage
                    src={selectedImage.mediaId}
                    alt={selectedImage.originalFilename || selectedImage.mediaId}
                    autoSize={true}
                    layout="fill"
                    objectFit="contain"
                    objectPosition="center"
                    aspectRatio={selectedAspectRatio}
                    onLoad={() => setFullImageLoaded(true)}
                    onError={() => setFullImageLoaded(true)}
                  />
                </div>

                {/* Loading spinner - positioned over the preview area */}
                {!fullImageLoaded && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                    }}
                  >
                    <div
                      style={{
                        width: '48px',
                        height: '48px',
                        border: '4px solid var(--ink-700)',
                        borderTopColor: 'var(--primary-600)',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                      }}
                    />
                    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                  </div>
                )}
              </div>

              {/* Metadata sidebar */}
              <div
                style={{
                  width: isCompactModal ? '100%' : '32%',
                  flex: isCompactModal ? '1 1 auto' : '0 0 32%',
                  maxWidth: isCompactModal ? undefined : '360px',
                  minWidth: isCompactModal ? 0 : '260px',
                  padding: '16px',
                  borderLeft: isCompactModal ? 'none' : '1px solid var(--line-soft)',
                  borderTop: isCompactModal ? '1px solid var(--line-soft)' : 'none',
                  overflowY: 'auto',
                  minHeight: 0,
                }}
              >
                <h3 style={{ marginTop: 0 }}>Asset Overview</h3>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ color: 'var(--ink-600)', fontSize: '12px' }}>Dimensions</label>
                  <div>{selectedImage.width} x {selectedImage.height} px</div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ color: 'var(--ink-600)', fontSize: '12px' }}>Format</label>
                  <div>{selectedImage.format}</div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ color: 'var(--ink-600)', fontSize: '12px' }}>Size</label>
                  <div>{selectedImage.size ? `${(selectedImage.size / 1024).toFixed(1)} KB` : '—'}</div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ color: 'var(--ink-600)', fontSize: '12px' }}>Upload Date</label>
                  <div>{new Date(selectedImage.uploadDate).toLocaleString()}</div>
                </div>

                {selectedImageAnalyticsLoading && (
                  <div style={{ marginBottom: '16px', color: 'var(--ink-600)', fontSize: '12px' }}>
                    Loading analytics...
                  </div>
                )}

                {selectedImageAnalyticsError && (
                  <div
                    style={{
                      marginBottom: '16px',
                      color: 'var(--danger-700)',
                      backgroundColor: 'var(--danger-100)',
                      padding: '8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                    }}
                  >
                    {selectedImageAnalyticsError}
                  </div>
                )}

                {selectedImageAnalytics && (
                  <>
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ color: 'var(--ink-600)', fontSize: '12px' }}>Optimized Deliveries</label>
                      <div>{selectedImageAnalytics.transformCount}</div>
                    </div>

                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ color: 'var(--ink-600)', fontSize: '12px' }}>Avg Optimization Time</label>
                      <div>{Math.round(selectedImageAnalytics.avgTransformTime)} ms</div>
                    </div>

                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ color: 'var(--ink-600)', fontSize: '12px' }}>Last Transformed</label>
                      <div>
                        {selectedImageAnalytics.lastTransformedAt
                          ? new Date(selectedImageAnalytics.lastTransformedAt).toLocaleString()
                          : '—'}
                      </div>
                    </div>

                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ color: 'var(--ink-600)', fontSize: '12px' }}>First Transformed</label>
                      <div>
                        {selectedImageAnalytics.firstTransformedAt
                          ? new Date(selectedImageAnalytics.firstTransformedAt).toLocaleString()
                          : '—'}
                      </div>
                    </div>

                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ color: 'var(--ink-600)', fontSize: '12px' }}>Top Output Sizes (30d)</label>
                      <div style={{ marginTop: '6px' }}>
                        {(selectedImageAnalytics.histogram?.bins || [])
                          .filter((bin) => bin.bin !== 'OTHER')
                          .slice(0, 6)
                          .map((bin) => (
                            <div key={bin.bin} style={{ fontSize: '11px', color: 'var(--ink-700)', marginBottom: '4px' }}>
                              {bin.bin} · {bin.count}
                            </div>
                          ))}
                        {(!selectedImageAnalytics.histogram?.bins || selectedImageAnalytics.histogram.bins.length === 0) && (
                          <div style={{ fontSize: '11px', color: 'var(--ink-600)' }}>No histogram data yet</div>
                        )}
                      </div>
                      {selectedImageAnalytics.histogram?.asOf ? (
                        <div style={{ marginTop: '6px', fontSize: '10px', color: 'var(--ink-500)' }}>
                          as of {new Date(selectedImageAnalytics.histogram.asOf).toLocaleString()}
                        </div>
                      ) : null}
                    </div>
                  </>
                )}

                {selectedImage.boundingBoxes && selectedImage.boundingBoxes.length > 0 && (
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ color: 'var(--ink-600)', fontSize: '12px' }}>Detected Faces</label>
                    <div>{selectedImage.boundingBoxes.length}</div>
                  </div>
                )}

                {selectedImage.objects && selectedImage.objects.length > 0 && (
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ color: 'var(--ink-600)', fontSize: '12px' }}>Detected Objects</label>
                    <div style={{ marginTop: '4px' }}>
                      {selectedImage.objects.map((obj, idx) => (
                        <span
                          key={idx}
                          style={{
                            display: 'inline-block',
                            backgroundColor: 'var(--primary-100)',
                            color: 'var(--primary-700)',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            marginRight: '4px',
                            marginBottom: '4px',
                          }}
                        >
                          {obj.class}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </MediaMagicProvider>
  );
}

export default Gallery;
